export type AssetCategory = 'boat' | 'car' | 'property' | 'apartment' | 'other';

export const CATEGORY_LABELS: Record<AssetCategory, string> = {
  boat: 'Boats',
  car: 'Cars',
  property: 'Properties',
  apartment: 'Apartment',
  other: 'Other',
};

export const CATEGORY_EMOJI: Record<AssetCategory, string> = {
  boat: '🚤',
  car: '🚗',
  property: '🏠',
  apartment: '🏢',
  other: '📌',
};

export interface Asset {
  id: string;
  owner_id: string;
  name: string;
  category: AssetCategory;
  description: string | null;
  location: string | null;
  photo_url: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export type RecurrenceType =
  | 'none'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'semiannually'
  | 'annually'
  | 'every_x_months'
  | 'every_x_years';

export const RECURRENCE_LABELS: Record<RecurrenceType, string> = {
  none: 'One-time',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiannually: 'Every 6 months',
  annually: 'Annually',
  every_x_months: 'Every X months',
  every_x_years: 'Every X years',
};

export interface Obligation {
  id: string;
  owner_id: string;
  asset_id: string | null;
  name: string;
  description: string | null;
  due_date: string; // YYYY-MM-DD
  recurrence_type: RecurrenceType;
  recurrence_interval: number | null;
  amount: number | null;
  payee: string | null;
  website: string | null;
  account_reference: string | null;
  notes: string | null;
  auto_pay: boolean;
  active: boolean;
  last_completed_date: string | null;
  google_event_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Reminder {
  id: string;
  obligation_id: string;
  offset_days: number;
  created_at: string;
}

export interface Completion {
  id: string;
  obligation_id: string;
  completed_date: string;
  original_due_date: string;
  amount: number | null;
  notes: string | null;
  status: 'completed' | 'skipped';
  created_at: string;
}

export type AttachmentCategory =
  | 'insurance'
  | 'registration'
  | 'title_deed'
  | 'warranty'
  | 'manual'
  | 'inspection'
  | 'receipt_bill'
  | 'tax'
  | 'contract'
  | 'permit'
  | 'appraisal'
  | 'photo'
  | 'other';

export const ATTACHMENT_CATEGORY_LABELS: Record<AttachmentCategory, string> = {
  insurance: 'Insurance',
  registration: 'Registration',
  title_deed: 'Title / Deed',
  warranty: 'Warranty',
  manual: 'Manual',
  inspection: 'Inspection Report',
  receipt_bill: 'Receipt / Bill',
  tax: 'Tax Document',
  contract: 'Contract',
  permit: 'Permit',
  appraisal: 'Appraisal',
  photo: 'Photo',
  other: 'Other',
};

export const ATTACHMENT_CATEGORY_ORDER: AttachmentCategory[] = [
  'contract',
  'title_deed',
  'insurance',
  'registration',
  'permit',
  'inspection',
  'appraisal',
  'warranty',
  'manual',
  'receipt_bill',
  'tax',
  'photo',
  'other',
];

export interface Attachment {
  id: string;
  obligation_id: string | null;
  asset_id: string | null;
  project_id: string | null;
  category: AttachmentCategory;
  file_name: string;
  storage_path: string;
  content_type: string | null;
  created_at: string;
}

export type ProjectStatus = 'idea' | 'planned' | 'in_progress' | 'done';

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  idea: 'Idea',
  planned: 'Planned',
  in_progress: 'In Progress',
  done: 'Done',
};

export const PROJECT_STATUS_ORDER: ProjectStatus[] = ['idea', 'planned', 'in_progress', 'done'];

export interface Project {
  id: string;
  owner_id: string;
  asset_id: string | null;
  name: string;
  status: ProjectStatus;
  target_timeframe: string | null;
  estimated_budget: number | null;
  actual_spent: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectTask {
  id: string;
  project_id: string;
  text: string;
  done: boolean;
  position: number;
  created_at: string;
}

export type Urgency = 'overdue' | 'due_soon' | 'ok';

// Smart defaults suggested when the user types a name for a new obligation.
export const SMART_DEFAULTS: {
  match: RegExp;
  recurrence: RecurrenceType;
  reminders: number[];
}[] = [
  { match: /insurance/i, recurrence: 'annually', reminders: [30, 7, 1] },
  { match: /registration/i, recurrence: 'annually', reminders: [30, 7] },
  { match: /inspection/i, recurrence: 'annually', reminders: [30, 7] },
  { match: /oil change/i, recurrence: 'every_x_months', reminders: [14] },
  { match: /property tax/i, recurrence: 'quarterly', reminders: [14] },
  { match: /(tax)/i, recurrence: 'annually', reminders: [30, 7] },
  { match: /(warranty)/i, recurrence: 'none', reminders: [60, 30, 7] },
];

export function getSmartDefault(name: string) {
  return SMART_DEFAULTS.find((d) => d.match.test(name));
}
